/** @jsxImportSource react */
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useFanforgeApi } from '@hotpot-arcade/fanforge-api-client';
import { type RecognitionContext, type UserTier } from '@hotpot-arcade/fanforge-core';
import { hc } from 'hono/client';
import { AppType } from '@hotpot-arcade/fanforge-api';

type FanforgeApiClient = ReturnType<typeof hc<AppType>>;

type RecognitionEntry = {
  userId: string;
  componentId: string;
  context: RecognitionContext;
  userTier?: UserTier;
  startTime: number;
  totalDuration: number;
};

type RecognitionDoseContextValue = {
  startTracking: (userId: string, componentId: string, context: RecognitionContext, userTier?: UserTier) => void;
  stopTracking: (userId: string, componentId: string) => void;
  isTracking: (userId: string, componentId: string) => boolean;
  flushData: () => Promise<void>;
  getTrackingStats: () => { activeCount: number; totalCount: number };
};

// Default value for the context to avoid null type
const defaultContextValue: RecognitionDoseContextValue = {
  startTracking: () => {
    console.warn('RecognitionDoseProvider not found. Tracking is disabled.');
  },
  stopTracking: () => {},
  isTracking: () => false,
  flushData: async () => {
    console.warn('RecognitionDoseProvider not found. Flush is disabled.');
  },
  getTrackingStats: () => ({ activeCount: 0, totalCount: 0 }),
};

const RecognitionDoseContext = React.createContext<RecognitionDoseContextValue>(defaultContextValue);

type RecognitionDoseProviderProps = {
  children: React.ReactNode;
  flushInterval?: number;
};

export function RecognitionDoseProvider({ children, flushInterval = 30000 }: RecognitionDoseProviderProps) {
  const { client: apiClient } = useFanforgeApi();
  const typedApiClient = apiClient as FanforgeApiClient;
  // Key format: `${userId}-${componentId}`
  const trackingMap = useRef<Map<string, RecognitionEntry>>(new Map());
  const activeTracking = useRef<Set<string>>(new Set());
  const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flushData = useCallback(async () => {
    const now = Date.now();
    const entriesToFlush = Array.from(trackingMap.current.values());
    if (entriesToFlush.length === 0) return;

    // 1. Atomically capture and reset the state
    const recognitions = entriesToFlush
      .map((entry) => {
        const key = `${entry.userId}-${entry.componentId}`;
        let duration = entry.totalDuration;

        if (activeTracking.current.has(key)) {
          duration += now - entry.startTime;
          // Reset for the next interval
          entry.startTime = now;
        }
        // Reset total duration for all flushed entries
        entry.totalDuration = 0;

        return {
          recognized_user_id: entry.userId,
          recognized_user_tier: entry.userTier || 'none',
          recognition_context: entry.context,
          seconds_viewed: Math.round(duration / 1000),
        };
      })
      .filter((item) => item.seconds_viewed > 0);

    // 2. Clean up non-active entries that have been flushed
    for (const entry of entriesToFlush) {
      const key = `${entry.userId}-${entry.componentId}`;
      if (!activeTracking.current.has(key)) {
        trackingMap.current.delete(key);
      }
    }

    if (recognitions.length === 0) {
      return;
    }

    // 3. Send the captured data
    try {
      const response = await typedApiClient.fanforge.recognition.$post({
        json: {
          recognitions,
        },
      });

      if (!response.ok) {
        console.error(
          `[RecognitionDose] Flush API error: ${response.status} ${response.statusText}`,
          await response.text()
        );
      } else {
        const result = await response.json();
        if (result.errors && result.errors.length > 0) {
          console.error('[RecognitionDose] Server-side processing errors:', result.errors);
        }
      }
    } catch (error) {
      console.error('[RecognitionDose] Failed to flush recognition dose data:', error);
    }
  }, [typedApiClient]);

  React.useEffect(() => {
    flushTimeoutRef.current = setInterval(flushData, flushInterval);

    return () => {
      if (flushTimeoutRef.current) {
        clearInterval(flushTimeoutRef.current);
      }
      flushData();
    };
  }, [flushData, flushInterval]);

  const startTracking = useCallback(
    (userId: string, componentId: string, context: RecognitionContext, userTier?: UserTier) => {
      const now = Date.now();
      const key = `${userId}-${componentId}`;

      if (!trackingMap.current.has(key)) {
        trackingMap.current.set(key, {
          userId,
          componentId,
          context,
          userTier,
          startTime: now,
          totalDuration: 0,
        });
      } else {
        const entry = trackingMap.current.get(key)!;
        if (!activeTracking.current.has(key)) {
          entry.startTime = now;
        }
        // Update tier if provided
        if (userTier) {
          entry.userTier = userTier;
        }
      }

      activeTracking.current.add(key);
    },
    []
  );

  const stopTracking = React.useCallback((userId: string, componentId: string) => {
    const key = `${userId}-${componentId}`;
    const entry = trackingMap.current.get(key);
    if (entry && activeTracking.current.has(key)) {
      const now = Date.now();
      const duration = now - entry.startTime;
      entry.totalDuration += duration;
    }

    activeTracking.current.delete(key);
  }, []);

  const isTracking = React.useCallback((userId: string, componentId: string) => {
    const key = `${userId}-${componentId}`;
    return activeTracking.current.has(key);
  }, []);

  const getTrackingStats = useCallback(() => {
    return {
      activeCount: activeTracking.current.size,
      totalCount: trackingMap.current.size,
    };
  }, []);

  const contextValue: RecognitionDoseContextValue = {
    startTracking,
    stopTracking,
    isTracking,
    flushData,
    getTrackingStats,
  };

  return React.createElement(RecognitionDoseContext.Provider, { value: contextValue }, children);
}

export function useRecognitionDoseContext() {
  return React.useContext(RecognitionDoseContext);
}
